"use client";

import React from "react";
import { Mail, Phone, MapPin, Loader, Send } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
const inputStyles = "border-border/50 placeholder:text-muted-foreground/50 focus-visible:border-secondary focus-visible:ring-secondary/20 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/20 flex h-10 w-full rounded-md border bg-transparent px-3 py-2 text-base shadow-sm transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-10 text-center animate-in fade-in zoom-in duration-500">
                <div className="mb-4 flex size-12 items-center justify-center rounded-lg bg-secondary/20 text-secondary">
                    <Send className="size-6" />
                </div>
                <h3 className="mb-2 font-default text-xl font-semibold text-foreground">Messaggio Inviato!</h3>
                <p className="mb-6 text-muted-foreground">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="border-secondary text-secondary hover:bg-secondary/10">
                    Invia un altro messaggio
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField control={control} name="name" rules={{
        required: "Il nome è obbligatorio"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground/90">Nome <span className="text-secondary">*</span></FormLabel>
                            <FormControl>
                                <input type="text" className={inputStyles} placeholder="Mario Rossi" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "L'email è obbligatoria",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Indirizzo email non valido"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground/90">Email <span className="text-secondary">*</span></FormLabel>
                            <FormControl>
                                <input type="email" className={inputStyles} placeholder="mario@azienda.it" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground/90">Telefono</FormLabel>
                            <FormControl>
                                <input type="tel" className={inputStyles} placeholder="+39 012 345 6789" {...field} />
                            </FormControl>
                            <FormDescription className="text-muted-foreground/60">Opzionale, per un contatto rapido</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground/90">Azienda</FormLabel>
                            <FormControl>
                                <input type="text" className={inputStyles} placeholder="Nome della tua azienda" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="message" rules={{
      required: "Il messaggio è obbligatorio"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-foreground/90">Come possiamo aiutarti? <span className="text-secondary">*</span></FormLabel>
                        <FormControl>
                            <Textarea placeholder="Descrivi le tue esigenze di irrigazione..." className="min-h-[120px] resize-y border-border/50 focus-visible:ring-secondary/20 focus-visible:border-secondary" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                    {submitError}
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-lg shadow-secondary/20">
                {isSubmitting ? <>
                        <Loader className="mr-2 size-4 animate-spin" />
                        Invio in corso...
                    </> : <>
                        <Send className="mr-2 size-4" />
                        Invia Richiesta
                    </>}
            </Button>
        </Form>;
}
export default function ContactMessageSection() {
  return <section data-section-id="222921" id="contattaci-oggi" className="dark relative overflow-hidden bg-background py-16 md:py-24">
            {}
            <div className="pointer-events-none absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 opacity-20">
                <svg width="600" height="600" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                    <path fill="var(--secondary)" d="M45.7,-76.1C58.9,-69.3,69.1,-55.3,77.5,-40.5C85.9,-25.7,92.5,-10.1,90.4,4.5C88.3,19.1,77.5,32.7,66.1,44.2C54.7,55.7,42.7,65.1,28.9,71.5C15.1,77.9,-0.5,81.3,-15.8,79.5C-31.1,77.7,-46.1,70.7,-58.3,60.1C-70.5,49.5,-79.9,35.3,-84.3,19.8C-88.7,4.3,-88.1,-12.5,-81.5,-26.8C-74.9,-41.1,-62.3,-52.9,-48.5,-59.5C-34.7,-66.1,-19.7,-67.5,-3.6,-62.4C12.5,-57.3,25,-45.7,32.5,-52.6C40,-59.5,32.5,-82.9,45.7,-76.1Z" transform="translate(100 100)" />
                </svg>
            </div>

            <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8">
                    
                    {}
                    <div className="flex flex-col justify-center lg:col-span-5">
                        <div className="mb-8">
                            <h2 className="font-default mb-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                                Pronto a risparmiare acqua?
                            </h2>
                            <p className="text-lg text-muted-foreground">
                                Contatta Solid Green Italia per una consulenza personalizzata. Scopri come le nostre tecnologie Solid Dew possono ottimizzare le tue risorse idriche.
                            </p>
                        </div>

                        <div className="space-y-6">
                            <div className="flex items-start gap-4"><div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/20 text-primary" data-ast-id="f:/src/pages/Home/sections/ContactMessageSection.tsx|sig:2bd2yp" data-ast-start="7945" data-ast-end="8651"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-mail size-5" aria-hidden="true" data-ast-id="f:/src/pages/Home/sections/ContactMessageSection.tsx|sig:606dlq" data-ast-start="8170" data-ast-end="8645"><path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" data-ast-id="f:/src/pages/Home/sections/ContactMessageSection.tsx|sig:bqap31" data-ast-start="8527" data-ast-end="8584"></path><rect x="2" y="4" width="20" height="16" rx="2" data-ast-id="f:/src/pages/Home/sections/ContactMessageSection.tsx|sig:4v2gp9" data-ast-start="8584" data-ast-end="8639"></rect></svg></div><div data-ast-id="f:/src/pages/Home/sections/ContactMessageSection.tsx|sig:2910lw" data-ast-start="8651" data-ast-end="9018"><h3 className="font-default text-base font-semibold text-foreground" data-ast-id="f:/src/pages/Home/sections/ContactMessageSection.tsx|sig:a5tlwn" data-ast-start="8776" data-ast-end="8975">Email</h3>info@solidgreenitalia.com<br /><br /></div></div>

                            <div className="flex items-start gap-4">
                                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/20 text-primary">
                                    <Phone className="size-5" />
                                </div>
                                <div>
                                    <h3 className="font-default text-base font-semibold text-foreground">Telefono</h3>
                                    <Link to="tel:+390123456789" className="text-muted-foreground transition-colors hover:text-secondary">+39 3520723180</Link>
                                    <p className="text-sm text-muted-foreground mt-1">Lun - Ven, 09:00 - 18:00</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/20 text-primary">
                                    <MapPin className="size-5" />
                                </div>
                                <div>
                                    <h3 className="font-default text-base font-semibold text-foreground">Sede Operativa</h3>
                                    <p className="text-muted-foreground">Mariano Comense, Como,&nbsp; Italia</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {}
                    <div className="lg:col-span-7">
                        <Card className="border-border/20 bg-card/40 shadow-2xl shadow-black/20 backdrop-blur-md rounded-2xl">
                            <CardHeader>
                                <CardTitle className="font-default text-2xl text-foreground">Inviaci un Messaggio</CardTitle>
                                <CardDescription className="text-muted-foreground">
                                    Compila il modulo sottostante e un nostro esperto ti ricontatterà entro 24 ore lavorative.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <FormProvider formKey="contact_form_home" formId="contact_form_home" sectionName="ContactMessageSection" defaultValues={{
                name: "",
                email: "",
                phone: "",
                subject: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true
                }
              }} submitText="Invia Richiesta" successMessage="Grazie per averci contattato! La tua richiesta è stata inoltrata con successo al team di Solid Green Italia." errorMessage="Si è verificato un errore durante l'invio. Per favore, riprova più tardi o contattaci via email.">
                                    <ContactFormInner />
                                </FormProvider>
                            </CardContent>
                        </Card>
                    </div>

                </div>
            </div>
        </section>;
}